import {
  ApiOutlined,
  AppstoreAddOutlined,
  ArrowLeftOutlined,
  AuditOutlined,
  BookOutlined,
  BulbOutlined,
  CaretRightOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CheckSquareOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ClusterOutlined,
  ControlOutlined,
  CopyOutlined,
  DeleteOutlined,
  DesktopOutlined,
  DiffOutlined,
  DisconnectOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileAddOutlined,
  FileProtectOutlined,
  FileSearchOutlined,
  FileTextOutlined,
  FormOutlined,
  ImportOutlined,
  InboxOutlined,
  KeyOutlined,
  LinkOutlined,
  LockOutlined,
  LoginOutlined,
  LogoutOutlined,
  MonitorOutlined,
  MoreOutlined,
  PaperClipOutlined,
  PauseCircleOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  PlusCircleOutlined,
  ProfileOutlined,
  RedoOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SaveOutlined,
  SearchOutlined,
  SelectOutlined,
  SettingOutlined,
  ShareAltOutlined,
  StopOutlined,
  UnlockOutlined,
  UserOutlined,
} from "@ant-design/icons";
import type { ReactNode } from "react";

/**
 * 语义命名规范（唯一真相源）:
 * 1) 使用英文小驼峰，例如 resetPassword/customFields。
 * 2) 语义只表达动作意图，不耦合业务对象名。
 * 3) 新增语义时要同时补齐:
 *    - SemanticAction union
 *    - SEMANTIC_ACTION_REGISTRY（icon + color）
 *    - 必要的 intent/key 推断规则（若依赖自动识别）
 * 4) 无法归类时不要猜，保持未定义，让界面暴露“未定义语义：xxx”后再补定义。
 */
export type SemanticAction = (typeof SEMANTIC_ACTION_KEYS)[number];

export const SEMANTIC_ACTION_KEYS = [
  "back",
  "select",
  "edit",
  "view",
  "viewBindings",
  "viewCatalog",
  "viewSnapshot",
  "viewExecutionSnapshot",
  "viewTopologyViewSnapshot",
  "manageCredentials",
  "open",
  "authorize",
  "openDirectorWorkspace",
  "openJudgeWorkspace",
  "openReviewWorkspace",
  "delete",
  "copy",
  "import",
  "generate",
  "create",
  "run",
  "pause",
  "resume",
  "stop",
  "archive",
  "download",
  "configure",
  "apply",
  "commit",
  "rejudge",
  "instantiate",
  "compare",
  "share",
  "resetPassword",
  "unlock",
  "lock",
  "impersonate",
  "kickout",
  "customFields",
  "publish",
  "viewEvidence",
  "recordReviewNote",
  "requestEvidence",
  "reject",
  "openConsole",
  "openVnc",
  "extend",
  "destroy",
  "viewTask",
  "heartbeat",
  "adoptVm",
  "releaseVm",
  "more",
] as const;

export type SemanticActionMeta = {
  icon: ReactNode;
  color: string;
};

export type AdminSemanticInput = {
  key: string;
  semantic?: SemanticAction | undefined;
  intent?: string | undefined;
  danger?: boolean | undefined;
};

export const SEMANTIC_ACTION_REGISTRY: Record<SemanticAction, SemanticActionMeta> = {
  back: {
    icon: <ArrowLeftOutlined />,
    color: "var(--ant-colorTextSecondary, rgba(0, 0, 0, 0.65))",
  },
  select: { icon: <CheckOutlined />, color: "var(--ant-colorPrimary, #1677ff)" },
  edit: { icon: <EditOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  view: { icon: <EyeOutlined />, color: "var(--ant-colorTextSecondary, rgba(0, 0, 0, 0.65))" },
  viewBindings: { icon: <SelectOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  viewCatalog: { icon: <BookOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  viewSnapshot: { icon: <FileTextOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  viewExecutionSnapshot: { icon: <ProfileOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  viewTopologyViewSnapshot: { icon: <ClusterOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  manageCredentials: { icon: <KeyOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  open: { icon: <LoginOutlined />, color: "var(--ant-colorPrimary, #1677ff)" },
  authorize: { icon: <SafetyCertificateOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  openDirectorWorkspace: { icon: <ControlOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  openJudgeWorkspace: { icon: <AuditOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  openReviewWorkspace: { icon: <FileProtectOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  delete: { icon: <DeleteOutlined />, color: "var(--ant-colorError, #ff4d4f)" },
  copy: { icon: <CopyOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  import: { icon: <ImportOutlined />, color: "var(--ant-colorSuccess, #52c41a)" },
  generate: { icon: <BulbOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  create: { icon: <PlusCircleOutlined />, color: "var(--ant-colorPrimary, #1677ff)" },
  run: { icon: <PlayCircleOutlined />, color: "var(--ant-colorSuccess, #52c41a)" },
  pause: { icon: <PauseOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  resume: { icon: <CaretRightOutlined />, color: "var(--ant-colorSuccess, #52c41a)" },
  stop: { icon: <PauseCircleOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  archive: { icon: <InboxOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  download: { icon: <DownloadOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  configure: { icon: <SettingOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  apply: { icon: <CheckSquareOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  commit: { icon: <SaveOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  rejudge: { icon: <RedoOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  instantiate: { icon: <AppstoreAddOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  compare: { icon: <DiffOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  share: { icon: <ShareAltOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  resetPassword: { icon: <ReloadOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  unlock: { icon: <UnlockOutlined />, color: "var(--ant-colorSuccess, #52c41a)" },
  lock: { icon: <LockOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  impersonate: { icon: <UserOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  kickout: { icon: <LogoutOutlined />, color: "var(--ant-colorError, #ff4d4f)" },
  customFields: { icon: <FormOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  publish: { icon: <CheckCircleOutlined />, color: "var(--ant-colorSuccess, #52c41a)" },
  viewEvidence: { icon: <SearchOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  recordReviewNote: { icon: <FileAddOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  requestEvidence: { icon: <PaperClipOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  reject: { icon: <CloseCircleOutlined />, color: "var(--ant-colorError, #ff4d4f)" },
  openConsole: { icon: <DesktopOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  openVnc: { icon: <MonitorOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  extend: { icon: <ClockCircleOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  destroy: { icon: <StopOutlined />, color: "var(--ant-colorError, #ff4d4f)" },
  viewTask: { icon: <FileSearchOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  heartbeat: { icon: <ApiOutlined />, color: "var(--ant-colorInfo, #1677ff)" },
  adoptVm: { icon: <LinkOutlined />, color: "var(--ant-colorSuccess, #52c41a)" },
  releaseVm: { icon: <DisconnectOutlined />, color: "var(--ant-colorWarning, #faad14)" },
  more: { icon: <MoreOutlined />, color: "var(--ant-colorTextSecondary, rgba(0, 0, 0, 0.65))" },
};

export function getSemanticActionMeta(action: SemanticAction): SemanticActionMeta {
  return SEMANTIC_ACTION_REGISTRY[action];
}

// key 推断只保留历史 lowercase key 的精确匹配；新代码优先显式传 semantic，减少歧义。
function inferSemanticActionByKey(key: string): SemanticAction | null {
  const rawKey = String(key || "").trim();
  if (!rawKey) return null;
  if (rawKey.includes("heartbeat")) return "heartbeat";
  if (rawKey.includes("binding")) return "viewBindings";
  if (rawKey.includes("catalog")) return "viewCatalog";
  if (rawKey.includes("execution-snapshot")) return "viewExecutionSnapshot";
  if (rawKey.includes("topology-view-snapshot")) return "viewTopologyViewSnapshot";
  if (rawKey.includes("runtime-snapshot") || rawKey.includes("topology-snapshot")) return null;
  if (rawKey.includes("snapshot")) return "viewSnapshot";
  if (rawKey.includes("credential")) return "manageCredentials";
  if (rawKey.includes("authorize") || rawKey.includes("authorization")) return "authorize";
  if (rawKey.includes("view-task")) return "viewTask";
  if (rawKey.includes("reconcile-unmanaged-vm") || rawKey.includes("adopt-vm")) return "adoptVm";
  if (rawKey.includes("release-vm") || rawKey.includes("unmanage-vm")) return "releaseVm";
  if (rawKey.includes("console")) return "openConsole";
  if (rawKey.includes("open-vnc") || rawKey.includes("vnc")) return "openVnc";
  if (rawKey === "director") return "openDirectorWorkspace";
  if (rawKey === "judge") return "openJudgeWorkspace";
  if (rawKey === "review") return "openReviewWorkspace";
  if (rawKey === "extend" || rawKey.includes("extend-")) return "extend";
  if (rawKey === "destroy" || rawKey.includes("destroy-")) return "destroy";
  if (rawKey.includes("view") || rawKey.includes("detail") || rawKey.includes("review"))
    return "view";
  if (rawKey.includes("edit") || rawKey.includes("update")) return "edit";
  if (rawKey.includes("delete") || rawKey.includes("remove") || rawKey.includes("recycle"))
    return "delete";
  if (rawKey.includes("archive")) return "archive";
  if (rawKey.includes("restore")) return "apply";
  if (rawKey.includes("reset-password") || rawKey.includes("password")) return "resetPassword";
  if (rawKey.includes("unlock")) return "unlock";
  if (rawKey.includes("lock")) return "lock";
  if (rawKey.includes("impersonate")) return "impersonate";
  if (rawKey.includes("kickout")) return "kickout";
  if (rawKey.includes("custom-fields")) return "customFields";
  if (rawKey.includes("publish")) return "publish";
  if (
    rawKey.includes("stop") ||
    rawKey.includes("disable") ||
    rawKey.includes("end") ||
    rawKey.includes("finish") ||
    rawKey.includes("terminate")
  )
    return "stop";
  if (rawKey.includes("enable") || rawKey.includes("activate") || rawKey.includes("run"))
    return "run";
  if (rawKey.includes("create") || rawKey.includes("add")) return "create";
  if (rawKey.includes("download")) return "download";
  if (rawKey.includes("select")) return "select";
  if (rawKey.includes("configure") || rawKey.includes("setting")) return "configure";
  if (rawKey.includes("compare")) return "compare";
  if (rawKey.includes("share")) return "share";
  if (rawKey.includes("copy")) return "copy";
  if (rawKey.includes("import")) return "import";
  if (rawKey.includes("generate")) return "generate";
  return null;
}

function inferSemanticActionByIntent(intent: string): SemanticAction | null {
  const normalized = String(intent || "").trim();
  if (!normalized) return null;
  if (normalized === "back") return "back";
  if (normalized === "create") return "create";
  if (normalized === "view") return "view";
  if (normalized === "view-bindings") return "viewBindings";
  if (normalized === "view-catalog") return "viewCatalog";
  if (normalized === "view-snapshot") return "viewSnapshot";
  if (normalized === "view-execution-snapshot") return "viewExecutionSnapshot";
  if (normalized === "view-topology-view-snapshot") return "viewTopologyViewSnapshot";
  if (normalized === "manage-credentials") return "manageCredentials";
  if (normalized === "edit") return "edit";
  if (normalized === "authorize") return "authorize";
  if (normalized === "delete") return "delete";
  if (normalized === "archive") return "archive";
  if (normalized === "download") return "download";
  if (normalized === "reset-password") return "resetPassword";
  if (normalized === "unlock") return "unlock";
  if (normalized === "lock") return "lock";
  if (normalized === "impersonate") return "impersonate";
  if (normalized === "kickout") return "kickout";
  if (normalized === "custom-fields") return "customFields";
  if (normalized === "publish") return "publish";
  if (normalized === "view-evidence") return "viewEvidence";
  if (normalized === "record-review-note") return "recordReviewNote";
  if (normalized === "request-evidence") return "requestEvidence";
  if (normalized === "reject") return "reject";
  if (normalized === "run") return "run";
  if (normalized === "pause") return "pause";
  if (normalized === "resume") return "resume";
  if (normalized === "stop") return "stop";
  if (normalized === "apply") return "apply";
  if (normalized === "select") return "select";
  if (normalized === "rejudge") return "rejudge";
  if (normalized === "commit") return "commit";
  if (normalized === "configure") return "configure";
  if (normalized === "instantiate") return "instantiate";
  if (normalized === "compare") return "compare";
  if (normalized === "share") return "share";
  if (normalized === "destroy") return "destroy";
  if (normalized === "extend") return "extend";
  if (normalized === "open-console") return "openConsole";
  if (normalized === "open-vnc") return "openVnc";
  if (normalized === "view-task") return "viewTask";
  if (normalized === "heartbeat") return "heartbeat";
  if (normalized === "adopt-vm") return "adoptVm";
  if (normalized === "release-vm") return "releaseVm";
  return null;
}

export function inferSemanticAction(input: AdminSemanticInput): SemanticAction | null {
  if (input.semantic) return input.semantic;
  const fromIntent = inferSemanticActionByIntent(String(input.intent || ""));
  if (fromIntent) return fromIntent;
  return inferSemanticActionByKey(input.key);
}

export function buildUndefinedSemanticLabel(input: AdminSemanticInput): string {
  return `未定义语义：${String(input.semantic || input.key)}`;
}

export function getSemanticActionPresentation(input: AdminSemanticInput): {
  semantic: SemanticAction | null;
  icon: ReactNode;
  color: string;
  unresolvedLabel?: string;
} {
  const semantic = inferSemanticAction(input);
  if (!semantic) {
    return {
      semantic: null,
      icon: <span style={{ fontSize: 10, fontWeight: 600 }}>未定义</span>,
      color: "var(--ant-colorError, #ff4d4f)",
      unresolvedLabel: buildUndefinedSemanticLabel(input),
    };
  }
  const meta = getSemanticActionMeta(semantic);
  if (!meta) {
    return {
      semantic: null,
      icon: <span style={{ fontSize: 10, fontWeight: 600 }}>未定义</span>,
      color: "var(--ant-colorError, #ff4d4f)",
      unresolvedLabel: buildUndefinedSemanticLabel(input),
    };
  }
  return { semantic, icon: meta.icon, color: meta.color };
}
